import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { IProjectAsset, AssetType, CharacterRole, CharacterRoleLabels, ImageAnalyzeMode, getLabelsByLocale, Locale } from '@packages/global';
import ImageUpload from '../common/ImageUpload';
import AudioUpload from '../common/AudioUpload';
import { useDialog } from '../../hooks/useDialog';
import { promptHelperApi } from '../../services/user/promptHelper.api';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface AssetFormProps {
  assetType: AssetType;
  editingAsset: IProjectAsset | null;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

interface FormData {
  name: string;
  prompt: string;
  conceptImageUrl: string;
  frontViewUrl: string;
  sideViewUrl: string;
  backViewUrl: string;
  detailImageUrl: string;
  description: string;
  artStyle: string;
  role: CharacterRole | '';
  voiceReference: string;
  sortOrder: number;
}

export default function AssetForm({ assetType, editingAsset, onSubmit, onCancel }: AssetFormProps) {
  const { t, i18n } = useTranslation(['projectSettings', 'common']);
  const { alert } = useDialog();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    prompt: '',
    conceptImageUrl: '',
    frontViewUrl: '',
    sideViewUrl: '',
    backViewUrl: '',
    detailImageUrl: '',
    description: '',
    artStyle: '',
    role: '',
    voiceReference: '',
    sortOrder: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (editingAsset) {
      setFormData({
        name: editingAsset.name || '',
        prompt: editingAsset.prompt || '',
        conceptImageUrl: editingAsset.conceptImageUrl || '',
        frontViewUrl: editingAsset.frontViewUrl || '',
        sideViewUrl: editingAsset.sideViewUrl || '',
        backViewUrl: editingAsset.backViewUrl || '',
        detailImageUrl: editingAsset.detailImageUrl || '',
        description: editingAsset.description || '',
        artStyle: editingAsset.artStyle || '',
        role: editingAsset.role || '',
        voiceReference: editingAsset.voiceReference || '',
        sortOrder: editingAsset.sortOrder || 0
      });
    }
  }, [editingAsset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证主体描述必填
    if (!formData.description || !formData.description.trim()) {
      await alert(t('asset.form.descriptionRequired'), t('asset.form.hint'), 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const cleanData: any = { name: formData.name, description: formData.description };
      if (formData.conceptImageUrl) cleanData.conceptImageUrl = formData.conceptImageUrl;
      if (formData.prompt) cleanData.prompt = formData.prompt;
      if (formData.artStyle) cleanData.artStyle = formData.artStyle;
      if (formData.frontViewUrl) cleanData.frontViewUrl = formData.frontViewUrl;
      if (formData.sideViewUrl) cleanData.sideViewUrl = formData.sideViewUrl;
      if (formData.backViewUrl) cleanData.backViewUrl = formData.backViewUrl;
      if (formData.detailImageUrl) cleanData.detailImageUrl = formData.detailImageUrl;
      if (formData.role && assetType === AssetType.CHARACTER) {
        cleanData.role = formData.role;
      }
      if (formData.voiceReference && assetType === AssetType.CHARACTER) {
        cleanData.voiceReference = formData.voiceReference;
      }
      if (formData.sortOrder !== undefined) cleanData.sortOrder = formData.sortOrder;

      await onSubmit(cleanData);
    } finally {
      setSubmitting(false);
    }
  };

  const getAssetTypeLabel = () => {
    switch (assetType) {
      case AssetType.CHARACTER:
        return t('asset.form.typeCharacter');
      case AssetType.PROP:
        return t('asset.form.typeProp');
      case AssetType.SCENE:
        return t('asset.form.typeScene');
      default:
        return t('asset.form.typeDefault');
    }
  };

  const removeImage = (field: keyof FormData) => {
    setFormData({ ...formData, [field]: '' });
  };

  const handleGeneratePrompt = async () => {
    if (!formData.conceptImageUrl) {
      await alert(t('asset.form.uploadConceptFirst'), t('asset.form.hint'), 'warning');
      return;
    }

    const modeMap: Record<AssetType, ImageAnalyzeMode> = {
      [AssetType.CHARACTER]: ImageAnalyzeMode.CHARACTER,
      [AssetType.PROP]: ImageAnalyzeMode.PROP,
      [AssetType.SCENE]: ImageAnalyzeMode.SCENE,
    };
    const mode = modeMap[assetType] ?? ImageAnalyzeMode.DEFAULT;

    setGenerating(true);
    try {
      const result = await promptHelperApi.analyzeImagePrompt(formData.conceptImageUrl, mode);
      setFormData({ ...formData, prompt: result.prompt });
    } catch (error: any) {
      await alert(error.message || t('asset.form.generatePromptFailed'), t('asset.form.error'), 'danger');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          {t('asset.form.nameLabel', { type: getAssetTypeLabel() })} <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
          placeholder={assetType === AssetType.CHARACTER ? t('asset.form.namePlaceholderCharacter') : assetType === AssetType.PROP ? t('asset.form.namePlaceholderProp') : t('asset.form.namePlaceholderScene')}
        />
      </div>

      {assetType === AssetType.CHARACTER && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">{t('asset.form.roleType')}</label>
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value as CharacterRole | '' })}
            className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
          >
            <option value="">{t('asset.form.selectRoleType')}</option>
            {Object.entries(getLabelsByLocale(CharacterRoleLabels, i18n.language as Locale)).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="border-t border-border-subtle pt-4">
        <h4 className="text-sm font-medium text-gray-200 mb-3">
          {t('asset.form.subjectDescription')} <span className="text-error">*</span>
        </h4>
        <textarea
          rows={3}
          required
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
          placeholder={
            assetType === AssetType.CHARACTER
              ? t('asset.form.descPlaceholderCharacter')
              : assetType === AssetType.PROP
                ? t('asset.form.descPlaceholderProp')
                : t('asset.form.descPlaceholderScene')
          }
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          {t('asset.form.artStyle')} <span className="text-gray-500">{t('asset.form.optional')}</span>
        </label>
        <textarea
          rows={2}
          value={formData.artStyle}
          onChange={(e) => setFormData({ ...formData, artStyle: e.target.value })}
          className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
          placeholder={t('asset.form.artStylePlaceholder')}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('asset.form.artStyleHint')}
        </p>
      </div>



      <div className="border-t border-border-subtle pt-4">
        <h4 className="text-sm font-medium text-gray-200 mb-3">
          {t('asset.form.conceptImages')}
        </h4>

        {/* 5个图片一排显示 */}
        <div className="grid grid-cols-5 gap-2">
          {/* 概念图 */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 text-center">
              {t('asset.form.imageConcept')}
            </label>
            {formData.conceptImageUrl ? (
              <div className="relative group ">
                <img
                  src={formData.conceptImageUrl}
                  alt={t('asset.form.conceptAlt')}
                  className="w-full h-full object-cover rounded border border-border-subtle cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setPreviewImage(formData.conceptImageUrl)}
                />
                <button
                  type="button"
                  onClick={() => removeImage('conceptImageUrl')}
                  className="absolute top-1 right-1 p-0.5 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="">
                <ImageUpload onUploadSuccess={(url, metadata) => {
                  const updates: Partial<FormData> = { conceptImageUrl: url };
                  if (metadata?.prompt && !formData.prompt) {
                    updates.prompt = metadata.prompt;
                  }
                  setFormData({ ...formData, ...updates });
                }} className="h-full" />
              </div>
            )}
          </div>

          {/* 正面 */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 text-center">{t('asset.form.imageFront')}</label>
            {formData.frontViewUrl ? (
              <div className="relative group ">
                <img
                  src={formData.frontViewUrl}
                  alt={t('asset.form.frontAlt')}
                  className="w-full h-full object-cover rounded border border-border-subtle cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setPreviewImage(formData.frontViewUrl)}
                />
                <button
                  type="button"
                  onClick={() => removeImage('frontViewUrl')}
                  className="absolute top-1 right-1 p-0.5 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="">
                <ImageUpload onUploadSuccess={(url, metadata) => {
                  const updates: Partial<FormData> = { frontViewUrl: url };
                  if (metadata?.prompt && !formData.prompt) {
                    updates.prompt = metadata.prompt;
                  }
                  setFormData({ ...formData, ...updates });
                }} className="h-full" />
              </div>
            )}
          </div>

          {/* 侧面 */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 text-center">{t('asset.form.imageSide')}</label>
            {formData.sideViewUrl ? (
              <div className="relative group ">
                <img
                  src={formData.sideViewUrl}
                  alt={t('asset.form.sideAlt')}
                  className="w-full h-full object-cover rounded border border-border-subtle cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setPreviewImage(formData.sideViewUrl)}
                />
                <button
                  type="button"
                  onClick={() => removeImage('sideViewUrl')}
                  className="absolute top-1 right-1 p-0.5 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="">
                <ImageUpload onUploadSuccess={(url, metadata) => {
                  const updates: Partial<FormData> = { sideViewUrl: url };
                  if (metadata?.prompt && !formData.prompt) {
                    updates.prompt = metadata.prompt;
                  }
                  setFormData({ ...formData, ...updates });
                }} className="h-full" />
              </div>
            )}
          </div>

          {/* 背面 */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 text-center">{t('asset.form.imageBack')}</label>
            {formData.backViewUrl ? (
              <div className="relative group ">
                <img
                  src={formData.backViewUrl}
                  alt={t('asset.form.backAlt')}
                  className="w-full h-full object-cover rounded border border-border-subtle cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setPreviewImage(formData.backViewUrl)}
                />
                <button
                  type="button"
                  onClick={() => removeImage('backViewUrl')}
                  className="absolute top-1 right-1 p-0.5 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="">
                <ImageUpload onUploadSuccess={(url, metadata) => {
                  const updates: Partial<FormData> = { backViewUrl: url };
                  if (metadata?.prompt && !formData.prompt) {
                    updates.prompt = metadata.prompt;
                  }
                  setFormData({ ...formData, ...updates });
                }} className="h-full" />
              </div>
            )}
          </div>

          {/* 细节 */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1 text-center">{t('asset.form.imageDetail')}</label>
            {formData.detailImageUrl ? (
              <div className="relative group ">
                <img
                  src={formData.detailImageUrl}
                  alt={t('asset.form.detailAlt')}
                  className="w-full h-full object-cover rounded border border-border-subtle cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setPreviewImage(formData.detailImageUrl)}
                />
                <button
                  type="button"
                  onClick={() => removeImage('detailImageUrl')}
                  className="absolute top-1 right-1 p-0.5 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="">
                <ImageUpload onUploadSuccess={(url, metadata) => {
                  const updates: Partial<FormData> = { detailImageUrl: url };
                  if (metadata?.prompt && !formData.prompt) {
                    updates.prompt = metadata.prompt;
                  }
                  setFormData({ ...formData, ...updates });
                }} className="h-full" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative">
        <label className="block text-sm font-medium text-gray-300 mb-1">{t('asset.form.drawingPrompt')}</label>
        <textarea
          rows={4}
          value={formData.prompt}
          onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
          className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
          placeholder={t('asset.form.drawingPromptPlaceholder', { type: getAssetTypeLabel() })}
        />
        <button
          type="button"
          onClick={handleGeneratePrompt}
          disabled={generating || !formData.conceptImageUrl}
          className="absolute bottom-2 right-2 p-1.5 text-primary hover:text-primary-light disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          title={t('asset.form.generateFromConcept')}
        >
          <SparklesIcon className={`h-5 w-5 ${generating ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {assetType === AssetType.CHARACTER && (
        <div className="border-t border-border-subtle pt-4">
          <h4 className="text-sm font-medium text-gray-200 mb-3">{t('asset.form.voiceReference')}</h4>
          <AudioUpload
            value={formData.voiceReference}
            showToneSelector={true}
            onUploadSuccess={(url) => setFormData({ ...formData, voiceReference: url })}
            placeholder={t('asset.form.voiceReferencePlaceholder')}
          />
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t border-border-subtle mt-4">
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? t('asset.form.submitting') : editingAsset ? t('asset.form.update') : t('asset.form.create')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 inline-flex justify-center py-2 px-4 border border-border-subtle shadow-sm text-sm font-medium rounded-md text-gray-300 bg-bg-elevated hover:bg-bg-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('common:cancel')}
        </button>
      </div>

      {/* 图片预览弹窗 */}
      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('asset.form.imagePreview')}
      />
    </form>
  );
}
